import { uniq } from 'lodash';
import moment from 'moment-timezone';
import { ById, OrderInfo, ProductApi } from 'common/types';
import { BookingPaymentState, BookingState, BookingPreparationState } from './types';
import { getOrderPayments } from '../atoms/paymentAmounts';
import { notUndefined } from 'common/utils/common';
import { isSalesPurchaseType } from 'common/modules/orders';

export const getCustomerName = (booking: OrderInfo): string => {
	return booking.shopperNames.filter((name) => Boolean(name))[0] ?? '';
};

export const getBookingState = (booking: OrderInfo): BookingState => {
	if (booking.rentalState !== 'COMPLETED' && isSalesPurchaseType(booking.purchaseType)) {
		return 'PENDING';
	}

	switch (booking.rentalState) {
		case 'BOOKED':
		case 'INIT': {
			if (booking.startDate <= moment().toISOString()) {
				return 'EXPIRED';
			} else {
				return 'UPCOMING';
			}
		}
		case 'ACTIVE': {
			return 'ACTIVE';
		}
		case 'COMPLETED': {
			return booking.activeState === 'RENT_CANCEL' ? 'CANCELLED' : 'COMPLETED';
		}
	}
};

export const getBookingPaymentState = (booking: OrderInfo): BookingPaymentState => {
	const chargePayment = getOrderPayments(booking).charge;
	const totalAmount = chargePayment.total;
	const unpaidAmount = chargePayment.unpaid;
	const refundedAmount = chargePayment.refunded;

	if (totalAmount === 0) return 'PAID';
	if (refundedAmount === totalAmount) return 'REFUNDED';

	return unpaidAmount > 0 ? 'UNPAID' : 'PAID';
};

export const getBookingPreparationState = (booking: OrderInfo): BookingPreparationState => {
	return booking.prepared ? 'PREPARED' : 'NOT_PREPARED';
};

export const getBookingPrice = (booking: OrderInfo): number => {
	return booking.pricing.total;
};

export const getBookingEndDate = (booking: OrderInfo): string | null => {
	return booking.endDateReturned ?? booking.returnTimeNext;
};

export const getBookingDeliveryId = (booking: OrderInfo) => {
	return booking.services?.delivery?.orderDeliveryId ?? '';
};

export const getProductsIncludedInBooking = (
	booking: OrderInfo,
	stockProductsById: ById<ProductApi>,
) => {
	return uniq(booking.stockProductIds)
		.map((productId) => stockProductsById[productId])
		.filter(notUndefined);
};
